import { FC, RefObject } from 'react';

import { formatCost } from 'utils/clickme';
import { TOP_UP_PAGE_ROUTE } from 'utils/constants/app';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import headerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/header/header_button_click';
import { ActivatorElementType } from '@hh.ru/magritte-internal-drop-base';
import { Link, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { ExclamationCircleFilledSize24, GiftOutlinedSize24, WalletOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useOnOffState from 'hooks/common/useOnOffState';

import FormatMoney from 'components/FormatMoney';

import BalanceReplenishmentModal from 'components/common/BalanceReplenishmentModal/BalanceReplenishmentModal';
import { HeaderBalanceDrop } from 'components/Header/HeaderBalanceDrop/HeaderBalanceDrop';

import { selectNeedTopUp } from 'store/models/stoppedCampaigns';
import { useSelector } from 'store/utils';

import styles from 'components/Header/HeaderBalance/HeaderBalance.module.less';

interface HeaderBalanceProps {
    dropActivatorRef: RefObject<ActivatorElementType>;
    isDropVisible: boolean;
    setIsDropVisibleOn: () => void;
    setIsDropVisibleOff: () => void;
}

export const HeaderBalance: FC<HeaderBalanceProps> = ({
    dropActivatorRef,
    isDropVisible,
    setIsDropVisibleOn,
    setIsDropVisibleOff,
}) => {
    const { realBalance, bonusBalance } = useSelector(({ balance }) => balance);
    const needTopUp = useSelector(selectNeedTopUp);
    const [
        isBalanceReplenishmentModalVisible,
        setIsBalanceReplenishmentModalVisibleOn,
        setIsBalanceReplenishmentModalVisibleOff,
    ] = useOnOffState(false);

    const { isMobile } = useBreakpoint();

    const onLinkToBalanceClick = () => headerButtonClick({ buttonName: 'balance_header' });

    return (
        <>
            <div className={styles.balanceWrapper}>
                <Link
                    Element={SPALink}
                    to={TOP_UP_PAGE_ROUTE}
                    onMouseEnter={isMobile ? undefined : setIsDropVisibleOn}
                    onClick={onLinkToBalanceClick}
                    data-qa="balance-header-button"
                >
                    <div className={styles.balanceLinkWrapper}>
                        <WalletOutlinedSize24 initialColor="contrast-secondary" />
                        <Text typography="label-2-regular">
                            <FormatMoney groupSeparator={NON_BREAKING_SPACE}>{realBalance}</FormatMoney>
                        </Text>
                    </div>
                </Link>
                {needTopUp && <ExclamationCircleFilledSize24 initialColor="negative" />}
            </div>
            <Link Element={SPALink} to={TOP_UP_PAGE_ROUTE} onClick={onLinkToBalanceClick} data-qa="bonus-header-button">
                <div className={styles.balanceLinkWrapper}>
                    <GiftOutlinedSize24 initialColor="contrast-secondary" />
                    <Text typography="label-2-regular">{formatCost(bonusBalance, NON_BREAKING_SPACE)}</Text>
                </div>
            </Link>
            <HeaderBalanceDrop
                dropActivatorRef={dropActivatorRef}
                isDropVisible={isDropVisible}
                setIsDropVisibleOff={setIsDropVisibleOff}
                setIsBalanceReplenishmentModalVisibleOn={setIsBalanceReplenishmentModalVisibleOn}
            />
            <BalanceReplenishmentModal
                withDescription={false}
                isVisible={isBalanceReplenishmentModalVisible}
                closeModal={setIsBalanceReplenishmentModalVisibleOff}
            />
        </>
    );
};
